import { ChangeDetectionStrategy, Component, ElementRef, Injector, Input, OnDestroy, OnInit, ViewChild, afterNextRender, inject, signal } from '@angular/core'

import { Lasku, Laskuasetukset, LaskuBase, LaskunumeroTyyppi } from '@lemon/core-firebase/model/lasku'
import { LaskuKorkoService } from '@lemon/core-firebase/service/lasku/lasku-korko.service'
import { AlvErittelynOsa, LaskunSummat, LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { TuettuKieli } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { IbanService } from '@lemon/core/service/iban.service'
import { StringService } from '@lemon/core/service/string.service'
import { WindowSizeService } from '../../../_jaettu-angular/service/window.service'

import { Big } from 'big.js'

import { BehaviorSubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export interface LaskuPdfEsikatselutiedot {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  asetukset: Laskuasetukset
  rajoitaKorkeuteen?: number
}

@Component({
  selector: 'lasku-pdf-esikatselu-perinteinen, [lasku-pdf-esikatselu-perinteinen]',
  styleUrls: ['./pdf.perinteinen.component.css'],
  templateUrl: './pdf.perinteinen.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LaskuPdfEsikatseluPerinteinen implements OnInit, OnDestroy {
  @ViewChild('esikatseluContainer', { static: true }) esikatseluContainer: ElementRef
  @ViewChild('kokopdflasku', { static: true }) kokopdflasku: ElementRef
  @ViewChild('alaosa', { static: true }) alaosa: ElementRef
  @ViewChild('ylaosa', { static: true }) ylaosa: ElementRef

  private _ngUnsubscribe = new Subject<void>()
  private _tiedot: LaskuPdfEsikatselutiedot = null
  private readonly _injector = inject(Injector)

  kieli: TuettuKieli = 'fi'
  transformStyleObservable: BehaviorSubject<string> = new BehaviorSubject('scale(' + 695 / 595 + ')')
  tuotteetMinimumHeightStyleObservable: BehaviorSubject<string> = new BehaviorSubject('277px')
  spacerHeightStyleObservable: BehaviorSubject<string> = new BehaviorSubject('842px')
  lataaminenValmisObservable: BehaviorSubject<boolean> = new BehaviorSubject(false)

  ulkoinenLasku: { nimi: string }
  alvErittely: AlvErittelynOsa[] = []
  virtuaaliviivakoodi = ''
  qrkoodi = ''
  mitatoity = false
  muistutus = false
  alvHuomautus: string = ''
  muotoiltuYtunnus: string = ''
  laskunSummat: LaskunSummat = null
  maksettuAiemmin: Big = null
  maksettavaaYhteensa: Big = null
  otsikko: string = ''
  iban: string = ''
  laskunumero: string = ''
  viitenumero: string = ''
  maa: string = ''
  laskuttajanMaa: string = ''
  maksuaikaa: number = 0
  pvm: string = ''
  toimituspvm: string = ''
  lokalisoituValuutta: string = ''
  lisatiedot: string = ''
  erapaiva: string = ''
  kateislasku = false
  viivastysprosentti: string = ''
  muistutushuomautus: string = null

  juurilasku: Lasku = null
  kasiteltava: LaskuBase = null
  asetukset: Laskuasetukset = null
  naytaProjekti = false

  rajoitaKorkeuteen: number = null
  readonly laskuLeftCoordinate = signal(2)

  @Input() naytaLatausmerkinta: boolean = true
  @Input() keskita: boolean = false
  @Input() laskunLahetysPiilotaProjekti: boolean = false

  get tiedot() {
    return this._tiedot
  }
  @Input()
  set tiedot(t: LaskuPdfEsikatselutiedot) {
    if (!t) {
      this.lataaminenValmisObservable.next(false)
    }

    // console.log('set TÄÄLLÄ')
    this.rajoitaKorkeuteen = t && t.rajoitaKorkeuteen ? t.rajoitaKorkeuteen : null
    // console.log('Set stuff', this.rajoitaKorkeuteen)

    this._tiedot = t
    this.juurilasku = t ? t.juurilasku : null
    this.kasiteltava = t ? t.kasiteltava : null
    this.asetukset = t ? t.asetukset : null

    this.naytaProjekti = !!t?.kasiteltava?.tuotteet?.some(tu => tu?.projekti) && !this.laskunLahetysPiilotaProjekti

    if (this.asetukset) {
      this.laskuttajanMaa = this.shared.annaLokalisoituMaa(this.asetukset.maakoodi, this.kasiteltava)
      if (this.asetukset.iban) {
        const trimmed = this.stringService.removeAllWhiteSpaces(this.asetukset.iban)
        this.iban = this.ibanService.formatoiIban(trimmed)
      } else {
        this.iban = ''
      }
    } else {
      this.laskuttajanMaa = ''
      this.iban = ''
    }

    if (this.juurilasku && this.kasiteltava) {
      this.ulkoinenLasku = this.shared.onkoUlkoinenLasku(this.kasiteltava) ? { nimi: this.shared.annaUlkoisenLaskunPalvelunNimi(this.kasiteltava.source, this.kasiteltava.kieli) } : null
      this.virtuaaliviivakoodi = this.shared.annaMuotoiltuVirtuaaliviivakoodi(this.juurilasku, this.kasiteltava, this.asetukset)
      this.qrkoodi = this.shared.annaMuotoiltuQrkoodi(this.juurilasku, this.kasiteltava, this.asetukset)
      this.muistutus = this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS
      this.mitatoity = this.shared.onkoKasiteltavaMitatoity(this.juurilasku, this.kasiteltava)
      this.alvHuomautus = this.shared.annaAlvHuomautus(this.kasiteltava)
      this.laskunSummat = this.shared.annaLaskunSummat(this.kasiteltava)
      this.muotoiltuYtunnus = this.shared.muotoileYtunnusLaskunTyypinPerusteella(this.kasiteltava, this.asetukset.ytunnus)

      if (this.muistutus) {
        const aiemminMaksettuTaiHyvitetty = this.kasiteltava.summaReskontraLaskunPvm + Math.abs(this.kasiteltava.summaHyvityksetLaskunPvm)
        this.maksettuAiemmin = new Big('0').minus(aiemminMaksettuTaiHyvitetty)
        this.maksettavaaYhteensa = this.laskunSummat.yhteensaKaikki.minus(aiemminMaksettuTaiHyvitetty)
        this.muistutushuomautus = this.shared.annaLokalisoituMuistutus(this.juurilasku, this.kasiteltava)
      } else {
        this.maksettuAiemmin = null
        this.muistutushuomautus = null
        this.maksettavaaYhteensa = this.laskunSummat.yhteensaKaikki
      }

      this.otsikko = this.shared.annaLokalisoituLaskunOtsikko(this.juurilasku, this.kasiteltava, this.maksettavaaYhteensa)
      this.laskunumero = this.shared.annaMuotoiltuLaskunumero(this.juurilasku, this.kasiteltava)
      this.viitenumero = this.shared.annaMuotoiltuViitenumero(this.juurilasku)
      this.maksuaikaa = this.shared.laskeMaksuaikaa(this.kasiteltava)
      this.pvm = this.shared.formatoiPvm(this.kasiteltava.pvml, this.kasiteltava.pvm, this.kasiteltava)
      this.toimituspvm = this.shared.formatoiPvm(this.kasiteltava.toimituspvml, this.kasiteltava.toimituspvm, this.kasiteltava)
      this.lokalisoituValuutta = this.shared.annaLokalisoituValuutanNimi(this.kasiteltava)
      if (this.kasiteltava.asiakas) {
        this.maa = this.shared.annaLokalisoituMaa(this.kasiteltava.asiakas.maa, this.kasiteltava)
      } else {
        this.maa = ''
      }
      this.kieli = this.kasiteltava.kieli
      this.lisatiedot = this.annaLisatiedot()
      this.alvErittely = this.shared.annaLaskunSummat(this.kasiteltava).alvErittely.filter(osa => osa.alvKanta.prosentti > 0)
      this.erapaiva = this.annaErapaiva()
      this.kateislasku = this.shared.onkoKateislasku(this.juurilasku, this.kasiteltava)

      const korkoprosentti = this.annaViivastyskorkoProsentti(this.juurilasku, this.kasiteltava)
      this.viivastysprosentti = this.currencyService.formatoiDesimaali(korkoprosentti, 2, this.kasiteltava.kieli) + ' %'
    } else {
      this.ulkoinenLasku = null
      this.virtuaaliviivakoodi = ''
      this.qrkoodi = ''
      this.muistutus = false
      this.mitatoity = false
      this.alvHuomautus = ''
      this.muotoiltuYtunnus = this.asetukset?.ytunnus ?? ''
      this.laskunSummat = null
      this.maksettuAiemmin = null
      this.maksettavaaYhteensa = null
      this.otsikko = ''
      this.laskunumero = ''
      this.viitenumero = ''
      this.maa = ''
      this.maksuaikaa = 0
      this.pvm = ''
      this.lokalisoituValuutta = ''
      this.kieli = 'fi'
      this.lisatiedot = ''
      this.alvErittely = []
      this.muistutushuomautus = null
      this.erapaiva = ''
      this.kateislasku = false
      this.viivastysprosentti = ''
    }

    this._laskeKunnesVakaa(() => this.lataaminenValmisObservable.next(true))
  }

  constructor(
    public shared: LaskuSharedService,
    private windowSizeService: WindowSizeService,
    private korkoService: LaskuKorkoService,
    private currencyService: CurrencyService,
    private stringService: StringService,
    private ibanService: IbanService,
  ) {
    this._laskeKunnesVakaa()
  }

  ngOnInit() {
    this.windowSizeService.sizeObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      // console.log('change size')
      this.laskeUudelleen()
      // setTimeout(() => {
      // console.log('change size 2')
      //   this.laskeUudelleen()
      // }, 0)
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private annaErapaiva(): string {
    if (!this.kasiteltava || this.mitatoity || this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
      return ''
    }
    return this.shared.annaPdfnErapaiva(this.kasiteltava)
  }

  private annaLisatiedot(): string {
    const lisatiedot = this.shared.annaLisatiedot(this.juurilasku, this.kasiteltava)
    return this.shared.valmisteleTeksti(lisatiedot)
  }

  private annaViivastyskorkoProsentti(juurilasku: Lasku, kasiteltava: LaskuBase): number {
    const prosentti = this.korkoService.annaViivastyskorkoProsentti(juurilasku, kasiteltava)
    return this.currencyService.muutaBigDecimalRahaksi(prosentti)
  }

  // Mittaukset riippuvat edellisen laskennan piirretyistä tyyleistä, joten lasketaan uudelleen piirron jälkeen, kunnes mikään ei enää muutu
  private _laskeKunnesVakaa(valmis?: () => void, kierroksia = 4) {
    afterNextRender(
      () => {
        if (this.laskeUudelleen() && kierroksia > 1) {
          this._laskeKunnesVakaa(valmis, kierroksia - 1)
        } else {
          valmis?.()
        }
      },
      { injector: this._injector },
    )
  }

  private laskeUudelleen(): boolean {
    let muuttui = this.paivitaEsikatselunTuotteidenKorkeusSpacer()

    const leveysEnnenTransformaatiota = this.esikatseluContainer.nativeElement.offsetWidth
    let uusiKerroin = Number(((leveysEnnenTransformaatiota - 4) / 595).toFixed(2))

    if (this.rajoitaKorkeuteen) {
      const suhdeluku = this.rajoitaKorkeuteen / 842
      const leveysOlisi = Number((595 * suhdeluku).toFixed(2))
      const rajoitusKerroin = Number(((leveysOlisi - 4) / 595).toFixed(2))
      // console.log('rajoitaKorkeuteen', this.rajoitaKorkeuteen, suhdeluku, leveysOlisi, rajoitusKerroin, uusiKerroin)
      if (rajoitusKerroin < uusiKerroin) {
        uusiKerroin = rajoitusKerroin
      }
      // console.log('LEVEYDET!!', leveysOlisi, uusiKerroin)
    }

    // console.log('leveys', leveysEnnenTransformaatiota)

    muuttui = this.paivitaEsikatselunKorkeusSpacer(uusiKerroin) || muuttui
    if (this.transformStyleObservable.value !== 'scale(' + uusiKerroin + ')') {
      // console.log('skaalauskerroin asetataan:', this.transformStyleObservable.value, uusiKerroin)
      this.transformStyleObservable.next('scale(' + uusiKerroin + ')')
      muuttui = true
    }

    // setTimeout(() => {
    //   this.paivitaEsikatselunKorkeusSpacer()
    // }, 0)
    return muuttui
  }

  annaRelatiivinenKorkeusmitta(mittaMillimetreissa: number): number {
    const suhdeluku = this.kokopdflasku.nativeElement.offsetWidth / 595
    const korkeusPikseleissa = (mittaMillimetreissa / 297) * 842
    return korkeusPikseleissa * suhdeluku
  }

  private paivitaEsikatselunTuotteidenKorkeusSpacer(): boolean {
    const ylaosanKorkeus = this.ylaosa ? this.ylaosa.nativeElement.offsetHeight : 225
    const alaosanKorkeus = this.alaosa ? this.alaosa.nativeElement.offsetHeight : 340
    const uusiSpacerKorkeus = Number((842 - (ylaosanKorkeus + alaosanKorkeus)).toFixed(0))
    // console.log('LASKE SPACER: ylaosa:', ylaosanKorkeus, this.ylaosa.nativeElement.offsetHeight, 'alaosa:', alaosanKorkeus, this.alaosa.nativeElement.offsetHeight, 'uusiSpacerKorkeus:', uusiSpacerKorkeus)
    const kaytettavaSpacerKorkeus = uusiSpacerKorkeus < 1 ? 10 : uusiSpacerKorkeus
    if (this.tuotteetMinimumHeightStyleObservable.value !== kaytettavaSpacerKorkeus + 'px') {
      // console.log('Spacerkorkeus asetetaan:', this.tuotteetMinimumHeightStyleObservable.value, kaytettavaSpacerKorkeus)
      this.tuotteetMinimumHeightStyleObservable.next(kaytettavaSpacerKorkeus + 'px')
      return true
    }
    return false
  }

  private paivitaEsikatselunKorkeusSpacer(uusiKerroin: number): boolean {
    let muuttui = false
    if (this.keskita) {
      const kokoLeveys = this.esikatseluContainer.nativeElement.offsetWidth
      const pdfLeveys = this.kokopdflasku.nativeElement.offsetWidth * uusiKerroin
      const ero = kokoLeveys - pdfLeveys
      const vasen = ero > 2 && ero / 2 > 2 ? Math.floor(ero / 2) : 2
      muuttui = vasen !== this.laskuLeftCoordinate()
      this.laskuLeftCoordinate.set(vasen)
      // console.log('KESKITTÄMINEN:', kokoLeveys, this.kokopdflasku.nativeElement.offsetWidth, uusiKerroin, pdfLeveys, ero, this.laskuLeftCoordinate)
    }

    const korkeusEnnenTransformaatiota = this.kokopdflasku.nativeElement.offsetHeight
    // console.log('KORKEUS ENNEN', korkeusEnnenTransformaatiota)
    const uusiKorkeus = Number((korkeusEnnenTransformaatiota * uusiKerroin).toFixed(0))
    const uusiKorkeusStr = uusiKorkeus.toFixed(0) + 'px'
    if (this.spacerHeightStyleObservable.value !== uusiKorkeusStr) {
      // console.log('UUSI KOKO KORKEUS', this.edellinenKorkeus, uusiKorkeus, (uusiKorkeus.toFixed(0)) + 'px')
      this.spacerHeightStyleObservable.next(uusiKorkeusStr)
      muuttui = true
    }
    return muuttui
  }
}
